import CardLoading from '@op/components/CardLoading';
import { FilterFixed, useFilterDataHook } from '@op/components/FilterCommon';
import { FilterGameInfoContext } from '@op/components/FilterCommon/context';
import { distinguishOfficialAccounts } from '@op/components/FilterCommon/utils/monitorUtil';
import useLanguage from '@op/hooks/useLanguage';
import { languageSelectOptions } from '@op/components/FilterBoxCommon/constant';
import TranslateSelect from '@op/components/SelectComponents/TranslateSelect';
import { LanguageType } from '@op/types/common';
import { DBO_TM } from '@op/utils/format';
import React, { useMemo, useRef, useState } from 'react';
import { Layout, Segment } from 'tea-component';
import { useLocation } from 'umi';
import useMenuTitle from '@opm/hooks/useMenuTitle';
import MarketContentBody from '@opm/components/MarketContentBody';
import { DBOM_POINT } from '@opm/operationLog/buriedPoint';
import {
  MahjongTrendingPublicationsPoint,
  ArrowsTrendingPublicationsPoint,
  Merge2TrendingPublicationsPoint,
  Merge3TrendingPublicationsPoint,
} from '@opm/operationLog/pages';
import {
  getZoneTypeFromPath,
  type TZoneType,
} from '../ZoneTopCreatives/constants';
import { useMultiGamesCond, TGameMap } from './hooks/useMultiGamesCond';
import ZoneTrendingVideo from './components/ZoneTrendingVideo';
import ZoneTrendingPosts from './components/ZoneTrendingPosts';
import './index.scss';

const { Content } = Layout;

type ValueType = 'official' | 'player' | 'total';

const ZONE_TRENDING_BURIED_POINT_MAP: Record<TZoneType, typeof MahjongTrendingPublicationsPoint> = {
  MahjongSolitaire: MahjongTrendingPublicationsPoint,
  Arrows: ArrowsTrendingPublicationsPoint,
  Merge2: Merge2TrendingPublicationsPoint,
  Merge3: Merge3TrendingPublicationsPoint,
};

export default function ZoneTrendingPublications() {
  const location = useLocation();
  const zoneType = getZoneTypeFromPath(location.pathname);
  const buriedPoint = zoneType
    ? ZONE_TRENDING_BURIED_POINT_MAP[zoneType]
    : MahjongTrendingPublicationsPoint;
  DBOM_POINT.useInitPage({ buriedPoint });
  const { currentTitle } = useMenuTitle();
  const { formatOptionsMessage } = useLanguage();
  const [language, setLanguage] = useState<LanguageType>('origin');
  const [value, setValue] = useState<ValueType>('total');

  // Step 1+2: marketing cond → multi_games_cond
  const {
    loading: condLoading,
    condData,
    zoneGames,
    games,
    gameMap,
  } = useMultiGamesCond({ pathname: location.pathname });

  // Inject zone_games into externalCondData so ZoneGameSelect widget can receive it
  const enrichedCondData = useMemo(() => {
    if (!condData || !zoneGames.length) return condData;
    return {
      ...condData,
      zone_games: zoneGames,
    };
  }, [condData, zoneGames]);

  // Opinion useFilterDataHook
  // 用 pathname 做 edition_unified_id，不同专区独立缓存
  // isNoRequest=true + externalCondData 跳过内部 cond 请求
  const fakeGameInfo = useMemo(() => {
    if (condLoading) return undefined;
    return {
      edition_unified_id: location.pathname,
      entity_type: 'mobile',
      id_type: 'unified',
    };
  }, [location.pathname, condLoading]);

  const {
    scrollBody,
    loading: filterLoading,
    filter,
    changeFilter,
    filterOptions,
    userSynchronousValue,
    confirmRef,
  } = useFilterDataHook({
    gameInfo: fakeGameInfo,
    pageName: 'Marketing_Trending_Publications',
    defaultUserSynchronousValue: {
      social_media_by_account: ['99999'],
      social_media_by_keyword: ['99999'],
      external_data_sources: ['99999'],
    },
  });

  // Derive gameMap subset based on zoneGames selected by filter
  // zoneGames is an array of game_id strings; [] means all
  const filteredGameMap = useMemo(() => {
    const selectedIds: string[] = filter?.zoneGames || [];
    if (selectedIds.length === 0) return gameMap;
    const selectedSet = new Set(selectedIds);
    const filtered: TGameMap = new Map();
    gameMap.forEach((val, key) => {
      if (selectedSet.has(key)) filtered.set(key, val);
    });
    return filtered;
  }, [filter?.zoneGames, gameMap]);

  const realFilter = useMemo(() => {
    const { zone_games: selectedIds, ...restFilter } = filter || {};
    const finalGames =
      selectedIds?.length > 0
        ? games.filter((g) => selectedIds.includes(g.edition_unified_id))
        : games;
    return {
      ...restFilter,
      games: finalGames,
      organization: value === 'total' ? '' : value,
    };
  }, [filter, games, value]);

  return (
    <FilterGameInfoContext.Provider
      value={{
        gameInfo: fakeGameInfo || {
          edition_unified_id: '',
          entity_type: 'mobile',
          id_type: 'unified',
        },
        use: !!fakeGameInfo,
        wholeGameInfo: {},
      }}
    >
      <Content className="dbom-zone-trending-page" ref={scrollBody}>
        <Content.Header title={currentTitle} />
        <MarketContentBody loading={condLoading}>
          <FilterFixed
            scrollBody={scrollBody}
            filter={filterOptions}
            confirmRef={confirmRef}
            userSynchronousValue={userSynchronousValue}
            defaultValue={DBO_TM.utcFilterRangeDay(6, 0)}
            defaultConfig={{
              channel: { channelKey: 'social' },
              keyWords: { hideSystemTab: true },
            }}
            isNoRequest
            externalCondData={enrichedCondData}
            confirm={(value: any) => {
              changeFilter(value);
            }}
            operateShowOptions={['more']}
          />

          <CardLoading loading={filterLoading}>
            <div className="dbo-filter-translateSelect">
              <Segment
                className="segment-light"
                value={value.toString()}
                onChange={(next: ValueType) => {
                  setValue(next);
                }}
                options={formatOptionsMessage([
                  { text: 'Total', value: 'total' },
                  { text: 'Official Account', value: 'official' },
                  { text: 'Earned Content', value: 'player' },
                ])}
              />
              <TranslateSelect
                langOptions={formatOptionsMessage(languageSelectOptions)}
                lang={language}
                isSingle
                getChange={(e: any) => setLanguage(e)}
              />
            </div>
            <ZoneTrendingVideo
              filter={realFilter}
              gameMap={filteredGameMap}
              language={language}
            />
            <ZoneTrendingPosts
              filter={realFilter}
              gameMap={filteredGameMap}
              language={language}
            />
          </CardLoading>
        </MarketContentBody>
      </Content>
    </FilterGameInfoContext.Provider>
  );
}
